import { useState } from "react";
import { useTheme } from "@/providers/ThemeProvider";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Moon, Sun, Monitor, Globe, Vibrate } from "lucide-react";
import { NotificationSettings } from "@/components/NotificationSettings";
import { LanguageSelector } from "@/components/LanguageSelector";
import { isHapticsEnabled, setHapticsEnabled } from "@/utils/haptics";

import { useTranslation } from "react-i18next";

export const PreferencesSettings = () => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [hapticsOn, setHapticsOn] = useState(isHapticsEnabled);

  const themeOptions = [
    { value: 'light' as const, label: t('settings.preferences.light'), icon: Sun },
    { value: 'system' as const, label: t('settings.preferences.system'), icon: Monitor },
    { value: 'dark' as const, label: t('settings.preferences.dark'), icon: Moon },
  ];

  return (
    <div className="space-y-4 pt-4">
      {/* Theme Setting */}
      <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
        <Label className="text-base font-medium">{t('settings.preferences.theme')}</Label>
        <p className="text-sm text-muted-foreground mb-3">{t('settings.preferences.theme_desc')}</p>

        <div className="grid grid-cols-3 gap-2">
          {themeOptions.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setTheme(value)}
              className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${theme === value
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
            >
              <Icon className={`w-5 h-5 ${theme === value ? 'text-primary' : 'text-muted-foreground'}`} />
              <span className={`text-sm font-medium ${theme === value ? 'text-foreground' : 'text-muted-foreground'}`}>
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Language Setting */}
      <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
        <Label className="text-base font-medium flex items-center gap-2">
          <Globe className="w-4 h-4" />
          {t('settings.preferences.language', 'Language')}
        </Label>
        <p className="text-sm text-muted-foreground mb-3">{t('settings.preferences.language_desc', 'Choose your preferred language')}</p>

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t('settings.preferences.select_language', 'Select language')}</span>
          <LanguageSelector />
        </div>
      </div>

      {/* Haptic Feedback */}
      <div className="p-4 rounded-lg bg-muted/30 border border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Label className="text-base font-medium flex items-center gap-2">
              <Vibrate className="w-4 h-4" />
              {t('settings.preferences.haptics')}
            </Label>
            <p className="text-sm text-muted-foreground mt-1">{t('settings.preferences.haptics_desc')}</p>
          </div>
          <Switch
            checked={hapticsOn}
            onCheckedChange={(checked) => {
              setHapticsOn(checked);
              setHapticsEnabled(checked);
            }}
          />
        </div>
      </div>

      {/* Goal Reminder Notifications */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-semibold mb-3 text-foreground">{t('settings.preferences.goal_reminders')}</h4>
        <NotificationSettings />
      </div>
    </div>
  );
};
